import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CircleAlert,
  CircleCheck,
  CircleX,
  Cloud,
  LoaderCircle,
  PlugZap,
  Save,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Button } from '../components/Button';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { Field, inputClass } from '../components/Field';
import { FormProblem } from '../components/FormProblem';
import { PageHeader } from '../components/PageHeader';
import { useToast } from '../components/Toast';
import { useAuth } from '../features/auth/AuthProvider';
import {
  b2Region,
  getStorage,
  reasonLabel,
  saveStorage,
  stepLabels,
  steps,
  storageKey,
  testStorage,
  toStorageProblem,
  type StorageBody,
  type StorageCheck,
  type StorageConfig,
  type StorageProblem,
} from '../features/platform/storage';
import { useTimeFormat } from '../lib/useTimeFormat';

const noProblem: StorageProblem = { fields: {}, form: null, check: null };

const emptyS3 = {
  endpoint: '',
  region: '',
  bucket: '',
  access_key_id: '',
  prefix: '',
  path_style: true,
  sse: false,
};

/**
 * Where the recordings of every company are kept (ADR-036): an S3-compatible
 * bucket such as Backblaze B2 (the disk is no longer offered, 0.67.1).
 * Super administrators only; a bucket is saved only after passing the
 * connection test.
 */
export function StoragePage() {
  const { request } = useAuth();
  const config = useQuery({ queryKey: storageKey, queryFn: () => getStorage(request) });

  return (
    <>
      <PageHeader
        title="Armazenamento"
        description="Bucket S3, como o Backblaze B2, onde ficam as gravações de todas as empresas."
      />
      <div className="max-w-3xl space-y-4 p-4 sm:p-6">
        {config.isPending && (
          <div aria-hidden="true" className="h-64 animate-pulse rounded-lg bg-surface-raised" />
        )}
        {config.isError && (
          <div className="rounded-lg border border-line bg-surface">
            <EmptyState
              tone="crit"
              icon={CircleAlert}
              title="Não foi possível carregar o armazenamento"
              description="Verifique a conexão com o servidor e tente novamente."
              action={
                <Button variant="primary" onClick={() => void config.refetch()}>
                  Tentar novamente
                </Button>
              }
            />
          </div>
        )}
        {config.data && (
          <StorageForm
            // Start over from what was saved.
            key={`${config.data.updated_at ?? 'env'}|${config.data.mode}`}
            saved={config.data}
          />
        )}
      </div>
    </>
  );
}

function StorageForm({ saved }: { saved: StorageConfig }) {
  const { request } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const fmt = useTimeFormat();
  // Still on the disk (no bucket yet): the form starts empty.
  const [s3, setS3] = useState(saved.s3.endpoint ? saved.s3 : emptyS3);
  const [secret, setSecret] = useState('');
  const [problem, setProblem] = useState<StorageProblem>(noProblem);
  const [check, setCheck] = useState<StorageCheck | null>(null);
  const [confirming, setConfirming] = useState(false);

  // The platform only saves a bucket (0.67.1).
  const body = (): StorageBody => ({ mode: 's3', s3: { ...s3, secret_access_key: secret } });
  const set = (patch: Partial<typeof s3>) => {
    setS3((v) => ({ ...v, ...patch }));
    setCheck(null);
  };

  const test = useMutation({
    mutationFn: () => testStorage(request, body()),
    onMutate: () => {
      setProblem(noProblem);
      setCheck(null);
    },
    onSuccess: setCheck,
    onError: (err) => {
      setProblem(toStorageProblem(err));
    },
  });
  const save = useMutation({
    mutationFn: () => saveStorage(request, body()),
    onMutate: () => {
      setProblem(noProblem);
    },
    onSuccess: (c) => {
      setConfirming(false);
      qc.setQueryData(storageKey, c);
      notify('Armazenamento salvo. As próximas gravações vão para o bucket.');
    },
    onError: (err) => {
      setConfirming(false);
      const p = toStorageProblem(err);
      setProblem(p);
      if (p.check) setCheck(p.check);
    },
  });

  const derived = b2Region(s3.endpoint);
  const changesDestination = saved.mode !== 's3';
  const busy = test.isPending || save.isPending;

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        setConfirming(true);
      }}
      className="space-y-4"
    >
      {saved.mode === 's3' ? (
        <InUse saved={saved} at={saved.updated_at ? fmt.absolute(saved.updated_at) : null} />
      ) : (
        <p
          role="status"
          className="flex items-start gap-2 rounded-lg border border-warn/40 bg-warn-soft p-4 text-[13px] text-fg"
        >
          <CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-warn" />
          Nenhum bucket configurado: as gravações ainda vão para o disco do servidor. Configure o
          bucket para gravar na nuvem.
        </p>
      )}

      <fieldset className="space-y-3 rounded-lg border border-line bg-surface p-4">
        <legend className="px-1 text-xs font-medium text-fg">Bucket S3</legend>
        <Field
          label="Endpoint"
          error={problem.fields.endpoint}
          hint="No Backblaze, aparece na página do bucket, como https://s3.us-west-004.backblazeb2.com."
        >
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              type="url"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              placeholder="https://s3.us-west-004.backblazeb2.com"
              value={s3.endpoint}
              onChange={(e) => {
                set({ endpoint: e.target.value });
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              className={`${inputClass} font-mono`}
            />
          )}
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Bucket" error={problem.fields.bucket}>
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                autoComplete="off"
                spellCheck={false}
                value={s3.bucket}
                onChange={(e) => {
                  set({ bucket: e.target.value });
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={`${inputClass} font-mono`}
              />
            )}
          </Field>
          <Field
            label="Região"
            error={problem.fields.region}
            hint={derived ? `Do endpoint do Backblaze: ${derived}.` : 'Opcional.'}
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                autoComplete="off"
                spellCheck={false}
                placeholder={derived ?? 'us-east-1'}
                value={s3.region}
                onChange={(e) => {
                  set({ region: e.target.value });
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={`${inputClass} font-mono`}
              />
            )}
          </Field>
          <Field
            label="ID da chave (keyID)"
            error={problem.fields.access_key_id}
            hint="Uma application key só deste bucket. A master key não funciona."
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                autoComplete="off"
                spellCheck={false}
                value={s3.access_key_id}
                onChange={(e) => {
                  set({ access_key_id: e.target.value });
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={`${inputClass} font-mono`}
              />
            )}
          </Field>
          <Field
            label="Chave secreta (applicationKey)"
            error={problem.fields.secret_access_key}
            hint={
              saved.secret_set
                ? 'Já cadastrada. Deixe em branco para manter.'
                : 'Guardada criptografada; não é exibida de novo.'
            }
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                type="password"
                autoComplete="new-password"
                placeholder={saved.secret_set ? '••••••••••••' : ''}
                value={secret}
                onChange={(e) => {
                  setSecret(e.target.value);
                  setCheck(null);
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={`${inputClass} font-mono`}
              />
            )}
          </Field>
        </div>
        <Field
          label="Pasta no bucket"
          error={problem.fields.prefix}
          hint="Opcional. Separa instalações que usam o mesmo bucket."
        >
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              autoComplete="off"
              spellCheck={false}
              placeholder="gravacoes"
              value={s3.prefix}
              onChange={(e) => {
                set({ prefix: e.target.value });
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              className={`${inputClass} font-mono`}
            />
          )}
        </Field>
        <div className="space-y-2 pt-1">
          <Check
            checked={s3.sse}
            onChange={(v) => {
              set({ sse: v });
            }}
            label="Criptografar as gravações no bucket (SSE-B2 no Backblaze)"
          />
          <Check
            checked={s3.path_style}
            onChange={(v) => {
              set({ path_style: v });
            }}
            label="Endereço por caminho (endpoint/bucket), o recomendado para o Backblaze"
          />
        </div>
      </fieldset>

      {check && <CheckResult check={check} />}
      <FormProblem message={problem.form} />

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          disabled={busy}
          onClick={() => {
            test.mutate();
          }}
        >
          {test.isPending ? (
            <LoaderCircle aria-hidden="true" className="size-4 animate-spin" />
          ) : (
            <PlugZap aria-hidden="true" className="size-4" strokeWidth={1.75} />
          )}
          {test.isPending ? 'Testando…' : 'Testar conexão'}
        </Button>
        <Button type="submit" variant="primary" disabled={busy}>
          <Save aria-hidden="true" className="size-4" strokeWidth={1.75} />
          {save.isPending ? 'Testando e salvando…' : 'Salvar'}
        </Button>
      </div>

      {confirming && (
        <ConfirmDialog
          title="Salvar o armazenamento"
          confirmLabel="Salvar"
          tone="default"
          busy={save.isPending}
          onConfirm={() => {
            save.mutate();
          }}
          onCancel={() => {
            setConfirming(false);
          }}
        >
          {changesDestination
            ? 'As próximas gravações de todas as empresas passam a ir para o bucket. As que já estão no disco continuam tocando até vencer.'
            : 'A nova configuração vale para as próximas gravações de todas as empresas, em poucos segundos.'}
          {' Antes de salvar, a conexão é testada de novo.'}
        </ConfirmDialog>
      )}
    </form>
  );
}

function InUse({ saved, at }: { saved: StorageConfig; at: string | null }) {
  return (
    <section
      aria-label="Em uso agora"
      className="flex items-start gap-3 rounded-lg border border-line bg-surface p-4"
    >
      <Cloud aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-accent" strokeWidth={1.75} />
      <div className="min-w-0 text-[13px]">
        <p className="text-fg">
          Em uso: <span className="font-medium">bucket {saved.s3.bucket}</span>
          <span className="ml-1 font-mono text-xs break-all text-fg-muted">
            {saved.s3.endpoint}
          </span>
        </p>
        <p className="mt-0.5 text-xs text-fg-subtle">
          {saved.source === 'environment'
            ? 'Configuração inicial da instalação. Ao salvar aqui, ela passa a ser gerenciada só por esta tela.'
            : `Salvo${saved.updated_by ? ` por ${saved.updated_by.name}` : ''}${at ? ` em ${at}` : ''}.`}
        </p>
      </div>
    </section>
  );
}

function Check({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2.5 text-[13px] text-fg">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
        className="size-4 accent-[var(--color-accent-strong)]"
      />
      {label}
    </label>
  );
}

/** The four steps of the test, the failing one with its reason. */
function CheckResult({ check }: { check: StorageCheck }) {
  const failedAt = check.ok ? steps.length : steps.indexOf(check.step ?? 'connect');
  return (
    <section
      role="status"
      aria-label="Resultado do teste"
      className={`rounded-lg border p-4 ${check.ok ? 'border-ok/40 bg-ok-soft' : 'border-crit/40 bg-crit-soft'}`}
    >
      <p className="text-[13px] font-medium text-fg">
        {check.ok ? 'Conexão aprovada' : 'A conexão não passou no teste'}
      </p>
      <ol className="mt-2 space-y-1">
        {steps.map((s, i) => (
          <Step key={s} state={i < failedAt ? 'ok' : i === failedAt ? 'fail' : 'skipped'}>
            {stepLabels[s]}
          </Step>
        ))}
      </ol>
      {!check.ok && <p className="mt-2 text-xs text-fg">{reasonLabel(check.reason)}</p>}
    </section>
  );
}

function Step({ state, children }: { state: 'ok' | 'fail' | 'skipped'; children: ReactNode }) {
  return (
    <li className="flex items-center gap-2 text-xs">
      {state === 'ok' && <CircleCheck aria-label="ok" className="size-3.5 text-ok" />}
      {state === 'fail' && <CircleX aria-label="falhou" className="size-3.5 text-crit" />}
      {state === 'skipped' && (
        <span
          aria-label="não executado"
          className="size-3.5 rounded-full border border-line-strong"
        />
      )}
      <span className={state === 'skipped' ? 'text-fg-subtle' : 'text-fg'}>{children}</span>
    </li>
  );
}
